import { useSyncExternalStore } from 'react';

/**
 * "A FULL-SCREEN STAGE IS ON THE SCREEN" — one bit, shared.
 *
 * WHY THIS EXISTS. `CallStage` is a fixed, full-screen takeover, but it is
 * rendered from inside `/app/empathy`, which is a `DashboardLayout` page. A
 * fixed overlay escapes the page's FLOW; it does not escape the page's
 * CHROME. On a phone the dashboard's bottom nav is `z-50` and the call stage
 * is `z-30`, so the nav painted straight over the control row and took its
 * taps: on 2026-09-14, on a real device, tapping End Call navigated to the
 * Course page and left the call running. Mic, camera and Speaker hit Tools,
 * Games and the nav strip. Every control on a video call was unreachable.
 *
 * The chrome stands down rather than the stage being raised over it, which is
 * what every other full-screen surface in this app does — the circle session,
 * the rehearsal and the AI buddy are all routes that simply never render
 * `DashboardLayout`. This flag is the version of that for a surface which is
 * rendered from INSIDE a dashboard page.
 *
 * The `z-40` queue pill reads it too, and returns null: it used to be the one
 * thing deliberately left above the stage (it was the other way back to the
 * queue), until it was seen sitting on the call's own words on a phone. The
 * stage carries everything it offered — End in the control row, the state and
 * the clock in the top bar — so during a call there is nothing left for it to
 * say.
 *
 * REF-COUNTED, because React 18+ StrictMode mounts an effect, tears it down
 * and mounts it again: a boolean would be left false after the teardown of
 * the first mount, and the chrome would come back under a live stage.
 */
let openCount = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Declare a full-screen stage open. Returns the release, which is safe to call
 * more than once — a double release must not un-hide the chrome underneath a
 * stage that is still up.
 */
export function openFullScreenStage(): () => void {
  openCount += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openCount -= 1;
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return openCount > 0;
}

/** Server render has no stage. */
function getServerSnapshot(): boolean {
  return false;
}

/** True while any full-screen stage is mounted. */
export function useFullScreenStageOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * The same bit, read outside React — for an event handler, which has to know
 * what is on the screen at the moment of the TAP rather than at the render
 * that installed it. The Back handler is the caller: whether a stage is up is
 * exactly what decides between minimising it and ordinary history back.
 */
export function isFullScreenStageOpen(): boolean {
  return getSnapshot();
}

/** Test seam: forget every open stage. */
export function resetFullScreenStage(): void {
  openCount = 0;
  emit();
}
